import React, { useState } from 'react';
import { 
  Building2, FileText, FileSpreadsheet, Download, Upload, CheckCircle2, 
  AlertCircle, Sparkles, RefreshCw, X, ArrowRight, ShieldCheck, Check, 
  Copy, Landmark, Archive, Layers, HelpCircle
} from 'lucide-react';

interface Branch {
  id: string;
  name: string;
  code?: string;
  city?: string;
}

export interface BudgetAmendmentResult {
  initiatorType: 'branch' | 'ncuvkz';
  targetYear: number;
  branchName: string;
  branchLetterNumber: string;
  branchLetterDate: string;
  summary: string;
  extractedJustification: string;
  dkaLetterBody: string;
  amendments: Array<{
    kekv: string;
    name: string;
    prevAmount: number;
    changeAmount: number;
    newAmount: number;
    monthlyChanges: Record<string, number>;
    justification: string;
  }>;
  totalDecreased: number;
  totalIncreased: number;
  netChange: number;
  docxUrl: string;
  docxFilename: string;
  pdfUrl?: string;
  pdfFilename?: string;
  xlsxUrl: string;
  xlsxFilename: string;
  zipUrl: string;
  zipFilename: string;
  complianceNotes: string[];
}

interface BudgetAmendmentFlowViewProps {
  language: 'uk' | 'en' | 'pl';
  enterpriseName?: string;
  enterpriseEdrpou?: string;
  branches: Branch[];
  parentOrg: string;
  currentUserEmail?: string;
  onOpenSignModal: (title: string, url: string) => void;
}

const DEMO_PRESET_1 = {
  title: 'ЦПОСІ та КНП (Дунаївці): Ремонт антени за рахунок електроенергії (2273 ➔ 2240)',
  branchName: 'Центр прийому і обробки спеціальної інформації та контролю навігаційного поля (ЦПОСІ та КНП)',
  year: 2026,
  text: `ЦЕНТР ПРИЙОМУ І ОБРОБКИ СПЕЦІАЛЬНОЇ ІНФОРМАЦІЇ ТА КОНТРОЛЮ НАВІГАЦІЙНОГО ПОЛЯ (ЦПОСІ та КНП)
Вих. № 124/05-18 від 14.04.2026 р.
Начальнику НЦУВКЗ Володимиру ПРИСЯЖНОМУ

Щодо внесення змін до кошторису та плану асигнувань на 2026 рік

Шановний Володимире Володимировичу!
У зв'язку з оптимізацією структури споживання енергоносіїв та проведенням комплексу заходів з енергозбереження на об'єктах Центру у м. Дунаївці, за підсумками І кварталу 2026 року утворилася економія коштів за КЕКВ 2273 "Оплата електроенергії" в сумі 120 000 (сто двадцять тисяч) гривень.
Водночас, за результатами поглибленої діагностики радіотехнічного комплексу виявлено критичний знос опорно-поворотного механізму антени станції "Дунаївці-1". Для недопущення зупинки прийому спеціальної супутникової інформації виникла нагальна потреба проведення невідкладного ремонту та технічного обслуговування за КЕКВ 2240 "Оплата послуг (крім комунальних)" на суму 120 000 гривень.
Просимо порушити клопотання перед Державним космічним агентством України щодо внесення змін до кошторису та плану асигнувань загального фонду бюджету на 2026 рік за КПКВК 6381010:
1. Зменшити видатки за КЕКВ 2273 на суму 120 000,00 грн (по 40 000,00 грн щомісяця у квітні, травні, червні 2026 р.).
2. Збільшити видатки за КЕКВ 2240 на суму 120 000,00 грн (по 40 000,00 грн щомісяця у квітні, травні, червні 2026 р.).
Перерозподіл не потребує виділення додаткових бюджетних асигнувань та не створить заборгованості.

Начальник ЦПОСІ та КНП         (підпис)       Олександр ПАВЛОВ
Головний бухгалтер             (підпис)       Ольга КРАВЧУК`
};

const DEMO_PRESET_2 = {
  title: 'ГЦСК (Городок): Датчики сейсмоконтролю за рахунок економії матеріалів (2210 ➔ 3110)',
  branchName: 'Головний центр спеціального контролю (ГЦСК)',
  year: 2026,
  text: `ГОЛОВНИЙ ЦЕНТР СПЕЦІАЛЬНОГО КОНТРОЛЮ (ГЦСК)
Вих. № 07/89 від 20.04.2026 р.
Начальнику НЦУВКЗ Володимиру ПРИСЯЖНОМУ

Щодо перерозподілу видатків розвитку та споживання на 2026 рік

Шановний Володимире Володимировичу!
Повідомляємо, що за результатами відкритих торгів на закупівлю витратних матеріалів (КЕКВ 2210) досягнуто економію коштів у сумі 150 000 грн. Разом з цим виникла нагальна потреба у придбанні двох високочутливих інфразвукових мікробарометрів для пункту спостереження PS-45 за КЕКВ 3110 "Придбання обладнання і предметів довгострокового користування".
Просимо порушити клопотання перед ДКА України про внесення змін до кошторису та плану асигнувань на 2026 рік шляхом зменшення КЕКВ 2210 на 150 000 грн та збільшення КЕКВ 3110 на 150 000 грн у ІІІ кварталі 2026 року.

Начальник ГЦСК                 (підпис)       Ігор ПРИХОДЬКО
Головний бухгалтер             (підпис)       Тетяна МЕЛЬНИК`
};

const DEMO_PRESET_3 = {
  title: 'Апарат НЦУВКЗ: Сервісні послуги за рахунок інших видатків (2800 ➔ 2240)',
  branchName: 'Центральний апарат НЦУВКЗ',
  year: 2026,
  text: `ДОПОВІДНА ЗАПИСКА
Начальнику НЦУВКЗ Володимиру ПРИСЯЖНОМУ

Щодо перерозподілу асигнувань апарату НЦУВКЗ на 2026 рік

Доповідаю, що за КЕКВ 2800 "Інші поточні видатки" за результатами оптимізації судових витрат наявні вільні залишки асигнувань у розмірі 65 000 грн. У зв'язку з необхідністю повірки лічильників обліку тепла та очищення систем кондиціонування серверної кімнати Центрального апарату, прошу порушити клопотання перед ДКА України щодо перенесення 65 000 грн з КЕКВ 2800 на КЕКВ 2240 "Оплата послуг (крім комунальних)" у травні-червні 2026 року.

Начальник відділу матеріально-технічного забезпечення   Сергій ВОВК`
};

export default function BudgetAmendmentFlowView({
  language,
  enterpriseName = 'Національний центр управління та випробувань космічних засобів',
  enterpriseEdrpou = '24507442',
  branches,
  parentOrg,
  currentUserEmail,
  onOpenSignModal
}: BudgetAmendmentFlowViewProps) {
  const isUk = language === 'uk';
  const currentYear = new Date().getFullYear();

  // Form State
  const [initiatorType, setInitiatorType] = useState<'branch' | 'ncuvkz'>('branch');
  const [selectedBranchName, setSelectedBranchName] = useState<string>(
    branches.length > 1 ? branches[1].name : 'Центр прийому і обробки спеціальної інформації та контролю навігаційного поля (ЦПОСІ та КНП)'
  );
  const [targetYear, setTargetYear] = useState<number>(currentYear);
  const [letterFiles, setLetterFiles] = useState<File[]>([]);
  const [letterText, setLetterText] = useState<string>('');
  const [customNotes, setCustomNotes] = useState<string>('');

  // Execution State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BudgetAmendmentResult | null>(null);
  const [copiedLetter, setCopiedLetter] = useState(false);

  const stepsList = [
    '1. Аналіз вхідного листа підрозділу та фіксація причин перерозподілу...',
    '2. Вилучення та систематизація фінансово-економічного обґрунтування...',
    '3. Складання офіційного листа НЦУВКЗ на Голову ДКА за ДСТУ 4163:2020...',
    '4. Розрахунок змін за кодами КЕКВ та помісячного плану асигнувань (Наказ № 57)...',
    '5. Генерація книги Excel (.xlsx з формулами), конвертація PDF та збирання ZIP для СЕДО...'
  ];

  const handleFillDemo = (preset: typeof DEMO_PRESET_1, type: 'branch' | 'ncuvkz') => {
    setInitiatorType(type);
    setSelectedBranchName(preset.branchName);
    setTargetYear(preset.year);
    setLetterText(preset.text);
  };

  const handleStartAmendment = async () => {
    if (!letterText.trim() && letterFiles.length === 0) {
      setError('Будь ласка, завантажте файл листа підрозділу (.docx, .pdf) або вставте його текст.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setResult(null);
    setLoadingStep(0);

    const stepInterval = setInterval(() => {
      setLoadingStep(prev => (prev < 4 ? prev + 1 : prev));
    }, 4500);

    const formData = new FormData();
    formData.append('initiatorType', initiatorType);
    formData.append('branchName', selectedBranchName);
    formData.append('targetYear', String(targetYear));
    formData.append('letterText', letterText);
    formData.append('customNotes', customNotes);
    formData.append('language', language);

    for (const f of letterFiles) {
      formData.append('files', f);
    }

    try {
      const res = await fetch('/api/enterprise/budget/amendment-from-letter', {
        method: 'POST',
        body: formData
      });
      clearInterval(stepInterval);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Помилка генерації пакета змін до кошторису');
      }
      const data = await res.json();
      setResult(data.result);
    } catch (err: any) {
      setError(err.message || 'Помилка виконання операції');
    } finally {
      clearInterval(stepInterval);
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedLetter(true);
    setTimeout(() => setCopiedLetter(false), 2000);
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Workflow Diagram Banner */}
      <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 shadow-lg relative overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
              Регламент ДКА України & ДКСУ
            </span>
            <span className="text-xs text-slate-400">
              Постанова КМУ № 228 (п. 47) та Інструкція Мінфіну № 57 (Додатки 11, 12)
            </span>
          </div>
          <span className="text-xs font-mono text-emerald-400 font-bold hidden sm:inline">
            Автоматизований ланцюг СЕДО
          </span>
        </div>

        {/* 5-step visual roadmap */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 pt-2">
          <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center">
            <div className="text-[10px] font-mono text-slate-400 uppercase">Крок 1</div>
            <div className="text-xs font-bold text-white mt-1 flex items-center justify-center gap-1">
              <Building2 className="w-3.5 h-3.5 text-blue-400 shrink-0" />
              <span>Лист Філії</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Клопотання з обґрунтуванням</p>
          </div>

          <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center">
            <div className="text-[10px] font-mono text-slate-400 uppercase">Крок 2</div>
            <div className="text-xs font-bold text-white mt-1 flex items-center justify-center gap-1">
              <Layers className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <span>ШІ-Аналіз НЦУВКЗ</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Вилучення суті та балансу КЕКВ</p>
          </div>

          <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center">
            <div className="text-[10px] font-mono text-slate-400 uppercase">Крок 3</div>
            <div className="text-xs font-bold text-white mt-1 flex items-center justify-center gap-1">
              <FileText className="w-3.5 h-3.5 text-purple-400 shrink-0" />
              <span>Лист на ДКА</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">ДСТУ 4163:2020 з перенесеним текстом</p>
          </div>

          <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center">
            <div className="text-[10px] font-mono text-slate-400 uppercase">Крок 4</div>
            <div className="text-xs font-bold text-white mt-1 flex items-center justify-center gap-1">
              <FileSpreadsheet className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span>Бланки Наказу № 57</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Додатки 11 & 12 (Excel формули)</p>
          </div>

          <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center">
            <div className="text-[10px] font-mono text-slate-400 uppercase">Крок 5</div>
            <div className="text-xs font-bold text-white mt-1 flex items-center justify-center gap-1">
              <Archive className="w-3.5 h-3.5 text-teal-400 shrink-0" />
              <span>ZIP для СЕДО</span>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">АСКОД / Megapolis / Вчасно</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Form Controls (7 cols) */}
        <div className="lg:col-span-7 space-y-6">

          {/* 1. Initiator & Target Year */}
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-black uppercase tracking-wider text-slate-300">
                1. Суб'єкт ініціювання змін та плановий рік
              </label>
              <span className="text-blue-400 text-xs font-mono">ГРБК: ДКА України</span>
            </div>

            {/* Radio / Toggle for Branch vs NCUVKZ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <button
                type="button"
                onClick={() => setInitiatorType('branch')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  initiatorType === 'branch'
                    ? 'bg-blue-600/20 border-blue-500 text-white shadow-md'
                    : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700'
                }`}
              >
                <div className="text-xs font-black flex items-center gap-1.5 text-blue-400">
                  <Building2 className="w-4 h-4" />
                  <span>Зміни для філії (підрозділу)</span>
                </div>
                <div className="text-[11px] text-slate-400 mt-1">
                  Лист від філії ➔ Подання НЦУВКЗ ➔ Затвердження ДКА для філії
                </div>
              </button>

              <button
                type="button"
                onClick={() => setInitiatorType('ncuvkz')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  initiatorType === 'ncuvkz'
                    ? 'bg-emerald-600/20 border-emerald-500 text-white shadow-md'
                    : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700'
                }`}
              >
                <div className="text-xs font-black flex items-center gap-1.5 text-emerald-400">
                  <Landmark className="w-4 h-4" />
                  <span>Зміни для апарату НЦУВКЗ</span>
                </div>
                <div className="text-[11px] text-slate-400 mt-1">
                  Доповідна записка ➔ Подання НЦУВКЗ ➔ Затвердження ДКА для центру
                </div>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {initiatorType === 'branch' ? (
                <div>
                  <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">
                    Відокремлений підрозділ (філія)
                  </label>
                  <select
                    value={selectedBranchName}
                    onChange={(e) => setSelectedBranchName(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white font-bold focus:outline-none focus:border-blue-500"
                  >
                    {branches.map(b => (
                      <option key={b.id} value={b.name}>{b.name} ({b.city || 'Україна'})</option>
                    ))}
                    <option value="Центр прийому і обробки спеціальної інформації та контролю навігаційного поля (ЦПОСІ та КНП)">
                      ЦПОСІ та КНП (м. Дунаївці)
                    </option>
                    <option value="Головний центр спеціального контролю (ГЦСК)">
                      ГЦСК (смт Городок)
                    </option>
                    <option value="Західний центр радіотехнічного спостереження (ЗЦРТС)">
                      ЗЦРТС (м. Мукачево)
                    </option>
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">
                    Юридична особа
                  </label>
                  <input
                    type="text"
                    disabled
                    value={enterpriseName}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-slate-300 font-bold"
                  />
                </div>
              )}

              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">
                  Бюджетний рік
                </label>
                <select
                  value={targetYear}
                  onChange={(e) => setTargetYear(Number(e.target.value))}
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white font-bold focus:outline-none focus:border-blue-500"
                >
                  <option value={currentYear}>{currentYear} рік (Поточний рік виконання)</option>
                  <option value={currentYear + 1}>{currentYear + 1} рік (Плановий рік)</option>
                  <option value={currentYear - 1}>{currentYear - 1} рік</option>
                </select>
              </div>
            </div>
          </div>

          {/* 2. Upload Branch Letter & Text Editor */}
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 shadow-lg space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="block text-xs font-black uppercase tracking-wider text-slate-300">
                  2. Лист підрозділу з обґрунтуванням потреби
                </span>
                <span className="text-[11px] text-slate-400">
                  Агент повністю запозичить це обґрунтування для офіційного листа на Голову ДКА
                </span>
              </div>

              {/* Demo Fill Buttons */}
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-slate-400 font-bold uppercase">Демо:</span>
                <button
                  type="button"
                  onClick={() => handleFillDemo(DEMO_PRESET_1, 'branch')}
                  className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 text-[10px] font-bold transition-all cursor-pointer"
                  title="ЦПОСІ та КНП: Ремонт антени за рахунок електроенергії"
                >
                  ЦПОСІ
                </button>
                <button
                  type="button"
                  onClick={() => handleFillDemo(DEMO_PRESET_2, 'branch')}
                  className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 text-[10px] font-bold transition-all cursor-pointer"
                  title="ГЦСК: Сейсмодатчики за рахунок економії матеріалів"
                >
                  ГЦСК
                </button>
                <button
                  type="button"
                  onClick={() => handleFillDemo(DEMO_PRESET_3, 'ncuvkz')}
                  className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 text-[10px] font-bold transition-all cursor-pointer"
                  title="Апарат НЦУВКЗ: Сервісні послуги"
                >
                  Апарат
                </button>
              </div>
            </div>

            {/* File Dropzone */}
            <div className="border-2 border-dashed border-slate-700 hover:border-blue-500 rounded-xl p-3.5 text-center transition-all bg-slate-950/40">
              <input
                type="file"
                multiple
                accept=".docx,.pdf,.xlsx,.txt"
                id="amendment-letter-upload"
                onChange={(e) => {
                  if (e.target.files) {
                    setLetterFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                  }
                }}
                className="hidden"
              />
              <label htmlFor="amendment-letter-upload" className="cursor-pointer block">
                <Upload className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                <span className="text-xs font-bold text-slate-200">
                  Підвантажити лист філії (.docx, .pdf, скан, .xlsx)
                </span>
                <span className="block text-[10px] text-slate-400 mt-0.5">
                  ШІ автоматично розпізнає текст та табличні додатки
                </span>
              </label>
            </div>

            {/* Attached Files list */}
            {letterFiles.length > 0 && (
              <div className="space-y-1.5">
                {letterFiles.map((file, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 bg-slate-950 border border-slate-800 rounded-lg text-xs"
                  >
                    <div className="flex items-center gap-2 truncate">
                      {file.name.endsWith('.xlsx') ? (
                        <FileSpreadsheet className="w-4 h-4 text-emerald-400 shrink-0" />
                      ) : (
                        <FileText className="w-4 h-4 text-blue-400 shrink-0" />
                      )}
                      <span className="font-mono text-slate-200 truncate">{file.name}</span>
                      <span className="text-[10px] text-slate-500 shrink-0">
                        ({(file.size / 1024).toFixed(0)} KB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setLetterFiles(prev => prev.filter((_, i) => i !== idx))}
                      className="text-slate-500 hover:text-red-400 p-1 cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Letter Text Editor */}
            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">
                Текст листа / службової записки (або розпізнаний зміст):
              </label>
              <textarea
                rows={7}
                value={letterText}
                onChange={(e) => setLetterText(e.target.value)}
                placeholder="Вставте сюди текст листа від філії (номер, дата, підстави, економія за одними КЕКВ та гостра потреба за іншими)..."
                className="w-full bg-slate-950 border border-slate-700 rounded-xl p-3 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500 font-mono leading-relaxed resize-none"
              />
            </div>

            {/* Additional notes */}
            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">
                Додаткові вказівки до листа на ДКА (опціонально):
              </label>
              <input
                type="text"
                value={customNotes}
                onChange={(e) => setCustomNotes(e.target.value)}
                placeholder="Наприклад: вказати терміновість у зв'язку з графіком запуску супутника або сесії спостереження..."
                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-between pt-1">
            <span className="text-[11px] text-slate-400">
              Постанова КМУ № 228 • Наказ Мінфіну № 57
            </span>

            <button
              type="button"
              disabled={isSubmitting}
              onClick={handleStartAmendment}
              className={`px-6 py-3.5 rounded-xl font-black text-sm flex items-center gap-2.5 shadow-xl transition-all cursor-pointer ${
                isSubmitting
                  ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-emerald-600 via-teal-600 to-blue-600 hover:from-emerald-500 hover:to-blue-500 text-slate-950 shadow-emerald-600/30'
              }`}
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin text-slate-900" />
                  <span>Формування подання на ДКА...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 text-slate-950" />
                  <span>Сформувати Лист на ДКА & Бланки Змін (№57)</span>
                </>
              )}
            </button>
          </div>

          {/* Loading Progress State */}
          {isSubmitting && (
            <div className="bg-slate-900 border border-emerald-500/40 rounded-2xl p-5 shadow-2xl">
              <div className="flex items-center justify-between text-xs text-emerald-300 font-bold mb-3">
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-emerald-400" />
                  <span>Фінансовий орган формує пакет документів...</span>
                </span>
                <span>Крок {loadingStep + 1} з {stepsList.length}</span>
              </div>
              <div className="w-full bg-slate-950 rounded-full h-2 mb-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-emerald-500 to-blue-500 h-full transition-all duration-700"
                  style={{ width: `${((loadingStep + 1) / stepsList.length) * 100}%` }}
                />
              </div>
              <p className="text-xs text-slate-300 font-medium animate-pulse">
                {stepsList[loadingStep]}
              </p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-950/80 border border-red-500/50 rounded-xl text-red-200 text-xs flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              <div>{error}</div>
            </div>
          )}

        </div>

        {/* Right Column: Previews & Downloads (5 cols) */}
        <div className="lg:col-span-5 space-y-6">

          {/* If no result yet: Guidance Card */}
          {!result && (
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 shadow-lg space-y-4">
              <div className="flex items-center gap-2 text-emerald-400">
                <HelpCircle className="w-4 h-4" />
                <span className="text-xs font-black uppercase tracking-wider text-slate-200">
                  Як працює модуль змін
                </span>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Коли підрозділ (наприклад, ЦПОСІ та КНП або ГЦСК) надсилає вам лист про необхідність перерозподілу коштів:
              </p>
              <ul className="space-y-2 text-xs text-slate-400">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-slate-200">1. Завантажте лист:</strong> файл або текст у форму ліворуч.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-slate-200">2. Агент вилучає суть:</strong> копіює економічне обґрунтування філії та адаптує під офіційне звернення до ДКА.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-slate-200">3. Готує 4 документи:</strong> офіційний лист на ДКА за підписом Начальника НЦУВКЗ, Додаток 11 (кошторис), Додаток 12 (план асигнувань) та паспорт для СЕДО.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-slate-200">4. Завантаження в СЕДО:</strong> завантажте сформований ZIP-пакет в один клік для реєстрації в системі документообігу.
                  </span>
                </li>
              </ul>
            </div>
          )}

          {/* Results Card */}
          {result && (
            <div className="bg-slate-900 border border-emerald-500/40 rounded-2xl p-6 shadow-2xl space-y-5 animate-fadeIn">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-xs font-black uppercase tracking-wider flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>Пакет змін готовий для СЕДО</span>
                </span>
                <span className="text-xs font-mono text-slate-400">
                  {result.targetYear} рік
                </span>
              </div>

              <div>
                <h3 className="text-sm font-black text-white">
                  {result.initiatorType === 'branch' ? `Зміни до кошторису: ${result.branchName}` : 'Зміни до кошторису апарату НЦУВКЗ'}
                </h3>
                <p className="text-xs text-slate-300 mt-1 leading-relaxed">{result.summary}</p>
              </div>

              {/* Financial Balance Strip */}
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-3 grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-[10px] text-slate-400 font-bold uppercase">Зменшення (-)</div>
                  <div className="text-xs font-black text-red-400 mt-0.5">
                    -{result.totalDecreased.toLocaleString('uk-UA')} грн
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-400 font-bold uppercase">Збільшення (+)</div>
                  <div className="text-xs font-black text-emerald-400 mt-0.5">
                    +{result.totalIncreased.toLocaleString('uk-UA')} грн
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-400 font-bold uppercase">Баланс змін</div>
                  <div className="text-xs font-black text-amber-300 mt-0.5">
                    {result.netChange === 0 ? '0.00 грн (1:1)' : `${result.netChange.toLocaleString('uk-UA')} грн`}
                  </div>
                </div>
              </div>

              {/* KEKV Shifts Table */}
              <div className="bg-slate-950/70 border border-slate-800 rounded-xl p-3 space-y-2">
                <div className="text-[10px] text-slate-400 font-bold uppercase flex items-center justify-between">
                  <span>Перерозподіл видатків за КЕКВ:</span>
                  <span className="text-blue-400 font-mono">№ {result.branchLetterNumber}</span>
                </div>

                <div className="space-y-1 text-xs">
                  {result.amendments.map((am, i) => (
                    <div key={i} className="flex items-center justify-between p-1.5 bg-slate-900/60 rounded-lg border border-slate-800/80">
                      <div className="truncate pr-2">
                        <span className="font-mono font-bold text-blue-400 mr-2">{am.kekv}</span>
                        <span className="text-slate-300 truncate">{am.name}</span>
                      </div>
                      <span className={`font-mono font-black shrink-0 ${am.changeAmount < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        {am.changeAmount > 0 ? `+${am.changeAmount.toLocaleString('uk-UA')}` : am.changeAmount.toLocaleString('uk-UA')} грн
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Borrowed Justification Preview */}
              <div className="bg-slate-950 border border-slate-800/80 rounded-xl p-3 space-y-1 text-xs">
                <div className="text-[10px] text-slate-400 font-bold uppercase">
                  Обґрунтування, запозичене з листа підрозділу:
                </div>
                <p className="text-slate-300 text-[11px] italic leading-relaxed line-clamp-3">
                  "{result.extractedJustification}"
                </p>
              </div>

              {/* Download Buttons Section */}
              <div className="space-y-2 pt-2 border-t border-slate-800">
                {/* 1. Primary SEDO ZIP Download */}
                <a
                  href={result.zipUrl}
                  download={result.zipFilename}
                  className="w-full py-3 px-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs rounded-xl shadow-xl shadow-emerald-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Archive className="w-4 h-4 text-slate-950" />
                  <span>📥 Завантажити Повний ZIP-пакет для СЕДО</span>
                </a>

                {/* 2. Official Word Letter */}
                <a
                  href={result.docxUrl}
                  download={result.docxFilename}
                  className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <FileText className="w-4 h-4" />
                  <span>Офіційний Лист на Голову ДКА (.docx за ДСТУ 4163)</span>
                </a>

                {/* 3. Excel Blanks Annex 11 & 12 */}
                <a
                  href={result.xlsxUrl}
                  download={result.xlsxFilename}
                  className="w-full py-2 px-4 bg-slate-800 hover:bg-slate-700 text-emerald-300 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer border border-emerald-500/30"
                >
                  <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
                  <span>Довідки про зміни (.xlsx Додатки 11 & 12 за Наказом № 57)</span>
                </a>

                {/* 4. PDF Letter if available */}
                {result.pdfUrl && (
                  <a
                    href={result.pdfUrl}
                    download={result.pdfFilename}
                    className="w-full py-1.5 px-4 bg-slate-950 hover:bg-slate-800 text-slate-300 font-medium text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer border border-slate-800"
                  >
                    <Download className="w-3.5 h-3.5 text-slate-400" />
                    <span>Лист у форматі PDF (для друку або КЕП)</span>
                  </a>
                )}

                {/* 5. Digital Signature Modal Trigger */}
                <button
                  type="button"
                  onClick={() => onOpenSignModal(
                    `Лист на ДКА про зміни до кошторису (${result.branchName})`,
                    result.pdfUrl || result.docxUrl
                  )}
                  className="w-full py-2 px-4 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/40 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <ShieldCheck className="w-4 h-4 text-purple-400" />
                  <span>Накласти Електронний Підпис (КЕП / Дія.Підпис)</span>
                </button>
              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
}
